import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  useSensor,
  useSensors,
  KeyboardSensor,
  closestCorners,
  PointerSensor,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import classnames from 'classnames';
import locale from '@glu/locale';
import useStyles from './ColumnSortableList.styles';
import SortContainer from './SortContainer';
import Column from '../Column/Column';
import type { IColumnsProps, IColumnState } from '../Columns/Columns';

interface IColumnSortableListProps {
  columnStates: IColumnState[];
  disableDrag: IColumnsProps['disableDrag'];
  disablePinning: IColumnsProps['disablePinning'];
  maxPrimary?: IColumnsProps['maxPrimary'];
  onShow: NonNullable<IColumnsProps['onShow']>;
  onMove: NonNullable<IColumnsProps['onMove']>;
  onMoveEnd: NonNullable<IColumnsProps['onMoveEnd']>;
  onPin: NonNullable<IColumnsProps['onPin']>;
}

const primarySortListId = 'primary-sort-list';
const otherSortListId = 'other-sort-list';

const ColumnSortableList = ({
  columnStates,
  disableDrag,
  disablePinning,
  maxPrimary,
  onMove,
  onMoveEnd,
  onPin,
  onShow,
}: IColumnSortableListProps) => {
  /** if I wrap this in useMemo there is a bug where the columns will be out of order */
  const [primaryColumns, otherColumns] = columnStates.reduce<IColumnState[][]>(
    (cols, c) => {
      if (c.primary) {
        cols[0].push(c);
      } else {
        cols[1].push(c);
      }
      return cols;
    },
    [[], []]
  );
  const [activeId, setActiveId] = useState<string>();
  const classes = useStyles();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 20,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const activeColumn = useMemo(
    () => columnStates.find((c) => c.colId === activeId),
    [activeId, columnStates]
  );
  const onDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveId(active.id as string);
  }, []);
  const onDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      const { id } = active;
      const { id: overId } = over!;
      const activeContainer = primaryColumns.find((c) => c.colId === id)
        ? primarySortListId
        : otherSortListId;
      /** Drag and drop proved to be difficult to test.
       Currently the test is hitting one half of the ternary branch
       but when the drag direction is switched the tests fail.
      */
      /* istanbul ignore next */
      const overContainer =
        overId === primarySortListId || primaryColumns.find((c) => c.colId === overId)
          ? primarySortListId
          : otherSortListId;
      if (
        !activeContainer ||
        !overContainer ||
        activeContainer === overContainer ||
        (maxPrimary && primaryColumns.length >= maxPrimary && overContainer === primarySortListId)
      ) {
        return;
      }

      onMove(id as string);
    },
    [maxPrimary, onMove, primaryColumns]
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const { id } = active;
      const { id: overId } = over!;
      const isPrimaryItems =
        overId === primarySortListId || primaryColumns.find((c) => c.colId === overId);
      const activeItems = primaryColumns.find((c) => c.colId === id)
        ? primaryColumns
        : otherColumns;
      /** Drag and drop proved to be difficult to test.
       Currently the test is hitting one half of the ternary branch
       but when the drag direction is switched the tests fail.
      */
      /* istanbul ignore next */
      const overItems = isPrimaryItems ? primaryColumns : otherColumns;
      const activeContainer = primaryColumns.find((c) => c.colId === id)
        ? primarySortListId
        : otherSortListId;
      /** Drag and drop proved to be difficult to test.
       Currently the test is hitting one half of the ternary branch
       but when the drag direction is switched the tests fail.
      */
      /* istanbul ignore next */
      const overContainer =
        overId === primarySortListId || primaryColumns.find((c) => c.colId === overId)
          ? primarySortListId
          : otherSortListId;
      if (
        maxPrimary &&
        primaryColumns.length >= maxPrimary &&
        activeContainer === otherSortListId &&
        overContainer === primarySortListId
      ) {
        return;
      }

      const activeIndex = activeItems.findIndex((c) => c.colId === id);
      const overIndex = overItems.findIndex((c) => c.colId === overId);
      /** Drag and drop proved to be difficult to test.
       Currently the test is hitting one half of the ternary branch
       but when the drag direction is switched the tests fail.
      */
      /* istanbul ignore next */
      const existingOrderedArray = isPrimaryItems ? otherColumns : primaryColumns;
      const newOrderedArray = arrayMove(overItems, activeIndex, overIndex);

      onMoveEnd(
        isPrimaryItems
          ? newOrderedArray.concat(existingOrderedArray)
          : existingOrderedArray.concat(newOrderedArray)
      );
      setActiveId(undefined);
    },
    [maxPrimary, onMoveEnd, otherColumns, primaryColumns]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hasPrimaryColumns = useMemo(() => !!columnStates.find((c) => c.primary !== undefined), []);
  return (
    <DndContext
      collisionDetection={closestCorners}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      sensors={sensors}
    >
      {!!maxPrimary && (
        <p className={classes.maxPrimaryLabel}>
          {locale.get('dataComponents.maxPrimary', `${maxPrimary}`)}
        </p>
      )}
      {hasPrimaryColumns ? (
        <SortContainer
          className={classes.primary}
          disabled={disableDrag}
          id={primarySortListId}
          items={primaryColumns}
        >
          {primaryColumns.map((c) => (
            <Column
              key={c.colId}
              columnState={c}
              disableDrag={disableDrag}
              disablePinning={disablePinning}
              onPin={onPin}
              onShow={onShow}
            />
          ))}
        </SortContainer>
      ) : null}
      <SortContainer
        className={classnames(classes.nonPrimary, { withNoPrimary: !hasPrimaryColumns })}
        disabled={disableDrag}
        id={otherSortListId}
        items={otherColumns}
      >
        {otherColumns.map((c) => (
          <Column
            key={c.colId}
            columnState={c}
            disableDrag={disableDrag}
            disablePinning={disablePinning}
            onPin={onPin}
            onShow={onShow}
          />
        ))}
      </SortContainer>

      {primaryColumns.length === 0 && otherColumns.length === 0 ? (
        <span className={classes.noResults}>{locale.get('dataComponents.column.none')}</span>
      ) : null}
      <DragOverlay>
        {activeColumn ? (
          <Column
            columnState={activeColumn}
            disableDrag={disableDrag}
            disablePinning={disablePinning}
            onPin={onPin}
            onShow={onShow}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ColumnSortableList;
